import dayjs from 'dayjs';
import type { StructureType } from '../types/types';
import { isJson } from './dataTypes';

export const isWeightModelField = (structure: StructureType): boolean => {
	if (structure?.Type !== 'model') return false;
	return structure.InnerStructure?.some((s) => s.Name === 'Weight') ?? false;
};

export interface LookupSource {
	url: string;
	filter: string;
	/**
	 * Inner model the lookup was resolved from (e.g. `Product` for
	 * `ResultProducts`); null when the field itself carries the foreign key.
	 * API filters must target `${field}.${subField}.Id` in that case.
	 */
	subField: string | null;
}

/**
 * Where a structure field's lookup options come from. Model fields carry
 * `ForeignKeyUrl` themselves, but array fields are link collections
 * (`ResultProducts` → `{ Id, Product, Percentage }`) and the backend only
 * reflects ForeignKey info onto the element's inner model — so fall back to
 * the InnerStructure entry that has one. Values are trimmed because the
 * structure JSONs carry stray trailing spaces.
 */
export const getLookupSource = (
	structure?: StructureType | null,
): LookupSource | null => {
	if (!structure) return null;
	if (structure.ForeignKeyUrl?.trim()) {
		return {
			url: structure.ForeignKeyUrl.trim(),
			filter: (structure.ForeignKeyDefaultFilter || '').trim(),
			subField: null,
		};
	}
	if (structure.Type === 'array') {
		const inner = structure.InnerStructure?.find(
			(item) => item.Type === 'model' && item.ForeignKeyUrl?.trim(),
		);
		if (inner) {
			return {
				url: inner.ForeignKeyUrl.trim(),
				filter: (inner.ForeignKeyDefaultFilter || '').trim(),
				subField: inner.Name,
			};
		}
	}
	return null;
};

export const getModelInfo = (filter: string): { id: string; name: string } => {
	const fallback = { id: '', name: '' };
	if (!filter || !isJson(filter)) return fallback;

	try {
		const parsed = JSON.parse(filter) as { fields?: string };
		const fieldsRaw = typeof parsed?.fields === 'string' ? parsed.fields : '';
		const parts = fieldsRaw.split(',');
		return {
			id: parts[0] ?? '',
			name: parts[1] ?? '',
		};
	} catch {
		return fallback;
	}
};

/**
 * Plain nested objects (e.g. Results `Laboratory`) are typed 'model' but have
 * no single display field — flatten their non-empty leaves into translated
 * "label: value" entries so cells can show a readable summary instead of a
 * raw JSON dump. Arrays are skipped: collections have their own columns.
 */
export function getObjectSummaryEntries(
	value: unknown,
	structure?: StructureType | null,
): string[] {
	const entries: string[] = [];
	const walk = (
		val: unknown,
		str: StructureType | null | undefined,
		label: string,
	) => {
		if (val === null || val === undefined || val === '') return;
		if (Array.isArray(val)) return;
		if (typeof val === 'object') {
			Object.entries(val as Record<string, unknown>).forEach(([key, child]) => {
				if (key === 'Id') return;
				const childStr = str?.InnerStructure?.find((s) => s.Name === key);
				const childLabel = childStr?.Translation?.trim() || key;
				walk(child, childStr, label ? `${label} ${childLabel}` : childLabel);
			});
			return;
		}
		let text: string;
		if (str?.Type === 'enum') {
			text = str.EnumValues?.[String(val)] ?? String(val);
		} else if (str?.Type === 'datetime') {
			text = dayjs(val as string).isValid()
				? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
				: String(val);
		} else {
			text = String(val);
		}
		entries.push(`${label}: ${text}`);
	};
	walk(value, structure, '');
	return entries;
}

export function displayValueByType(value: any, structure: StructureType) {
	try {
		return displayValueByTypeInner(value, structure);
	} catch {
		return typeof value === 'object' && value !== null
			? JSON.stringify(value)
			: (value as string | number);
	}
}

function displayValueByTypeInner(value: any, structure: StructureType) {
	let val = value;

	if (val === null || val === undefined) {
		val = '-';
	} else if (structure?.Type === 'model') {
		if (isWeightModelField(structure)) {
			if (typeof val === 'object' && !Array.isArray(val)) {
				val = (val as Record<string, unknown>).Weight ?? '-';
			}
		} else if (typeof val === 'object' && !Array.isArray(val)) {
			const { name: nameVar } = getModelInfo(
				structure.ForeignKeyDefaultFilter || '',
			);
			const displayed = nameVar
				? (val as Record<string, unknown>)[nameVar]
				: undefined;
			val =
				displayed !== null && displayed !== undefined
					? String(displayed)
					: JSON.stringify(val);
		} else {
			val = String(val);
		}
	} else if (structure?.Type === 'array') {
		const { name: nameVar } = getModelInfo(
			structure?.ForeignKeyDefaultFilter || '',
		);
		val = Array.isArray(val)
			? val.map((item: any) => item[nameVar]).join(', ')
			: JSON.stringify(val);
	} else if (typeof val === 'object') {
		val = JSON.stringify(val);
	} else if (structure?.Type === 'boolean') {
		val = val ? 'Taip' : 'Ne';
	} else if (structure?.Type === 'enum') {
		val = structure?.EnumValues[value];
	} else if (['int32', 'int16', 'double'].includes(structure?.Type)) {
		val = value;
	} else if (structure?.Type === 'datetime') {
		val = value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-';
	} else if (structure?.Type === 'string') {
		val = value;
	} else {
		val = value;
	}
	if (val !== null && typeof val === 'object') {
		return JSON.stringify(val);
	}
	return val as string | number;
}
